import { Profile, ShowAssignment } from '../types';

/**
 * The caller's effective standing on **one specific show**.
 *
 * A port of `mobile_app/lib/permissions/show_actor.dart` - keep the two in
 * step. Deliberately **not** ordered: do not compare these, do not index them
 * into a list, do not treat one as "including" another. The backend is
 * non-linear in two places a role ladder cannot express:
 *
 * - `isStudentLeaderOfShow` (`functions/src/api/shows/permissions.ts`) is
 *   false for teachers and admins - a super admin is not "a leader with more".
 * - `canManageShow` needs a per-show assignment for teachers but not for org
 *   admins.
 *
 * `AuthContext.hasRole` models roles as a ladder. That is fine for the
 * dashboard's org-level sections and wrong for anything inside a show, which
 * is why show pages resolve through this instead.
 */
export type ShowActor =
  | 'none'
  | 'student'
  | 'showLeader'
  | 'showStaff'
  | 'orgAdmin'
  | 'superAdmin';

type ActorProfile = Pick<Profile, 'role' | 'organizationId' | 'showAssignments'>;

/** The assignment `profile` holds for `showId`, or null. */
export function assignmentFor(
  profile: ActorProfile | null | undefined,
  showId: string | null | undefined,
): ShowAssignment | null {
  if (!profile || !showId) return null;
  return profile.showAssignments?.find(a => a.showId === showId) ?? null;
}

// A null show organisation means the caller did not supply one, so the leg is
// skipped - the server re-checks the real match on every call.
function organisationMatches(profile: ActorProfile, showOrganizationId?: string | null): boolean {
  if (showOrganizationId == null) return true;
  return profile.organizationId === showOrganizationId;
}

/**
 * The caller's actor for `showId`. Fails closed: anything missing or
 * unrecognised is `'none'`.
 *
 * Admins are decided before the assignment lookup because they are authorised
 * without one; deciding them afterwards would resolve an org admin to `none`
 * on every show they are not personally assigned to.
 */
export function actorForShow(
  profile: ActorProfile | null | undefined,
  showId: string | null | undefined,
  showOrganizationId?: string | null,
): ShowActor {
  if (!profile) return 'none';

  if (profile.role === 'super_admin') return 'superAdmin';

  // The role type still carries a legacy `admin`, but no backend helper
  // accepts it (canManageShow and canViewStudentEmails check `org_admin`), so
  // it resolves to `none` below rather than offering controls that would fail.
  if (profile.role === 'org_admin') {
    return organisationMatches(profile, showOrganizationId) ? 'orgAdmin' : 'none';
  }

  const assignment = assignmentFor(profile, showId);
  if (!assignment) return 'none';

  if (profile.role === 'teacher') {
    return organisationMatches(profile, showOrganizationId) ? 'showStaff' : 'none';
  }

  // Leadership is a property of the assignment, not the role. No production
  // path writes `role: 'student_leader'` - it is accepted as a legacy alias
  // only, and never on its own grants leadership.
  if (profile.role === 'student' || profile.role === 'student_leader') {
    return assignment.isLeader === true ? 'showLeader' : 'student';
  }

  return 'none';
}

/** Teacher, org admin or super admin. Profile shape, not authorisation. */
export function isStaffRole(role: string | undefined | null): boolean {
  return role === 'teacher' || role === 'org_admin' || role === 'admin' || role === 'super_admin';
}

/** Student or the legacy student_leader role. Profile shape, not authorisation. */
export function isStudentRole(role: string | undefined | null): boolean {
  return role === 'student' || role === 'student_leader';
}
